import { useLocation } from "@tanstack/react-router";
import React, { useEffect } from "react";

type TTitleConfig = {
  defaultTitle?: string;
  titleMap?: Record<string, string>;
  capitalize?: boolean;
};

const BRAND_NAME = "Fundy";

function getAppTitle(location: { pathname: string }, config: TTitleConfig = {}) {
  const { titleMap = {}, capitalize = true } = config;

  // remove leading and trailing slashes and split into segments
  const segments = location.pathname.split("/").filter(Boolean);

  // get the last non-UUID segment (UUIDs are dynamic IDs, not meaningful labels)
  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const meaningfulSegments = segments.filter((s) => !UUID_PATTERN.test(s));
  const lastSegment = meaningfulSegments[meaningfulSegments.length - 1];

  // For home page, return just the brand name
  if (!lastSegment) return BRAND_NAME;

  // check if we have a custom mapping for this path
  const fullPath = segments.join("/");
  let pageTitle: string | undefined;
  if (titleMap[fullPath]) {
    pageTitle = titleMap[fullPath];
  } else if (titleMap[lastSegment]) {
    pageTitle = titleMap[lastSegment];
  } else {
    // format the segment
    let title = lastSegment.replace(/[-_]/g, " ");

    if (capitalize) {
      title = title.replace(/\b\w/g, (char) => char.toUpperCase());
    }

    pageTitle = title;
  }

  // Format as "Fundy | page name" unless it already starts with "Fundy"
  if (pageTitle.toLowerCase().startsWith(BRAND_NAME.toLowerCase())) {
    return pageTitle;
  }

  return `${BRAND_NAME} | ${pageTitle}`;
}

type TUseDocTitleProps = {
  deps: React.DependencyList;
  options: TTitleConfig;
};

export function useDocTitle(props: TUseDocTitleProps = { deps: [], options: {} }) {
  const location = useLocation();

  const { deps, options } = props;

  // once the page component renders, update HTML document's title
  useEffect(() => {
    const previousTitle = document.title;

    document.title = getAppTitle(location, options);

    return function () {
      document.title = previousTitle;
    };
  }, [...deps /* eslint-disable-line react-hooks/exhaustive-deps */, location]);
}
